<template>
  <div class="account">
    <div v-if="!guestStore.guestUser" class="account__signed-out">
      <p>{{ $t("booking.log_in_to_see_bookings") }}</p>
      <GuestAuthPanel @authenticated="loadAppointments" />
    </div>

    <template v-else>
      <header class="account__header">
        <h1>{{ $t("booking.your_bookings") }}</h1>
        <v-btn variant="text" @click="logout">{{ $t("booking.log_out") }}</v-btn>
      </header>

      <v-card v-for="appointment in appointments" :key="appointment.id" class="account__card">
        <v-card-title>{{ appointment.services.map((s) => s.name).join(', ') }}</v-card-title>
        <v-card-subtitle>
          {{ formatTime(appointment.start_time) }} &middot; {{ appointment.company.title }}
        </v-card-subtitle>
        <v-card-text>
          <p><strong>{{ $t("booking.reference") }}</strong> {{ appointment.booking_reference }}</p>
          <p><strong>{{ $t("booking.status") }}</strong> {{ appointment.status }}</p>
        </v-card-text>
        <v-card-actions v-if="appointment.status === 'created' || appointment.status === 'confirmed'">
          <v-btn size="small" variant="text" class="account__cancel-btn" @click="cancel(appointment)">
            {{ $t("booking.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <p v-if="!appointments.length" class="account__empty">{{ $t("booking.no_bookings_yet") }}</p>

      <v-card v-if="preferences" class="account__preferences">
        <v-card-title>{{ $t("booking.email_preferences") }}</v-card-title>
        <v-card-text>
          <v-switch
            v-model="preferences.confirmations"
            :label="$t('booking.booking_confirmations')"
            density="comfortable"
            @update:model-value="savePreferences"
          />
          <v-switch
            v-model="preferences.reminders"
            :label="$t('booking.appointment_reminders')"
            density="comfortable"
            @update:model-value="savePreferences"
          />
          <v-switch
            v-model="preferences.promotions"
            :label="$t('booking.promotions')"
            density="comfortable"
            @update:model-value="savePreferences"
          />
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";
import {
  cancelGuestAppointment,
  getEmailPreferences,
  listGuestAppointments,
  logoutGuest,
  updateEmailPreferences,
} from "../../services/public-booking-api.service";
import { EmailPreferences, GuestAppointment } from "../../types/public-booking.type";
import { useGuestStore } from "../../stores/guest.store";
import GuestAuthPanel from "./GuestAuthPanel.vue";

const guestStore = useGuestStore();
const appointments = ref<GuestAppointment[]>([]);
const preferences = ref<EmailPreferences | null>(null);

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const loadAppointments = async () => {
  if (!guestStore.guestUser) return;
  const { appointments: list } = await listGuestAppointments();
  appointments.value = list;
  preferences.value = await getEmailPreferences();
};

const savePreferences = async () => {
  if (!preferences.value) return;
  try {
    preferences.value = await updateEmailPreferences(preferences.value);
  } catch {
    // The global axios interceptor already surfaces the server's error message.
  }
};

const cancel = async (appointment: GuestAppointment) => {
  try {
    await cancelGuestAppointment(appointment.id);
    await loadAppointments();
  } catch {
    // The global axios interceptor already surfaces the server's error message.
  }
};

const logout = async () => {
  await logoutGuest();
  guestStore.setGuestUser(null);
  appointments.value = [];
};

onMounted(loadAppointments);
</script>

<style lang="scss" scoped>
@use "../../styles/_breakpoints" as bp;

.account {
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px;

  @include bp.tablet {
    padding: 16px 12px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    @include bp.small-phone {
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  &__card {
    margin-bottom: 12px;
  }

  &__cancel-btn {
    @include bp.tablet {
      min-height: 44px;
    }
  }

  &__empty {
    color: #888;
  }
}
</style>
